import { useEffect } from 'react';

const scrollTo = (href: string) => {
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
};

export default function HeroSection() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.hero-animate');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="inicio" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://static.readdy.ai/image/45c2ae79549b129e69766d02f53aa268/d6c843e5998ca5bec2e63b5343953f21.png"
          alt="Flota moderna Yakis"
          className="w-full h-full object-cover object-center"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-yakis-navy/70 via-yakis-navy/50 to-yakis-navy/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-yakis-navy/60 via-transparent to-yakis-navy/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 section-container pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-3xl">
          {/* Label */}
          <div className="hero-animate animate-on-scroll mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yakis-gold/20 border border-yakis-gold/30 text-yakis-gold text-xs font-medium tracking-wider uppercase backdrop-blur-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-yakis-gold animate-pulse" />
              Transporte Empresarial Premium
            </span>
          </div>

          {/* Main Title */}
          <h1 className="hero-animate animate-on-scroll font-display font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[1.1] mb-6">
            Movilidad
            <br />
            <span className="text-yakis-gold">Empresarial</span>
            <br />
            de Clase Mundial
          </h1>

          {/* Subtitle */}
          <p className="hero-animate animate-on-scroll text-base sm:text-lg md:text-xl text-white/70 max-w-xl mb-10 leading-relaxed">
            Más de 20 años transportando al personal de empresas líderes en 
            San Luis Potosí y Monterrey. Seguridad, puntualidad y calidad garantizadas.
          </p>

          {/* CTAs */}
          <div className="hero-animate animate-on-scroll flex flex-col sm:flex-row gap-4 mb-16">
            <a
              href="#cotizacion"
              onClick={(e) => {
                e.preventDefault();
                scrollTo('#cotizacion');
              }}
              className="btn-primary text-base py-4 px-8"
            >
              <i className="ri-file-list-3-line" />
              Solicitar Cotización
            </a>
            <a
              href="#contacto"
              onClick={(e) => {
                e.preventDefault();
                scrollTo('#contacto');
              }}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-white/30 text-white font-medium rounded-full transition-all duration-300 hover:bg-white/10 hover:border-white/50 whitespace-nowrap"
            >
              <i className="ri-phone-line" />
              Contáctanos
            </a>
          </div>

          {/* Stats Row */}
          <div className="hero-animate animate-on-scroll grid grid-cols-3 gap-4 md:gap-8 max-w-lg">
            <div className="text-center sm:text-left">
              <p className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-yakis-gold">20+</p>
              <p className="text-xs sm:text-sm text-white/50 mt-1">Años de experiencia</p>
            </div>
            <div className="text-center sm:text-left border-x border-white/10 px-4 md:px-8">
              <p className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-yakis-gold">2</p>
              <p className="text-xs sm:text-sm text-white/50 mt-1">Ciudades cubiertas</p>
            </div>
            <div className="text-center sm:text-left">
              <p className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-yakis-gold">100%</p>
              <p className="text-xs sm:text-sm text-white/50 mt-1">Puntualidad</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-xs text-white/40 tracking-wider uppercase">Scroll</span>
        <div className="w-6 h-10 border-2 border-white/20 rounded-full flex items-start justify-center p-1.5">
          <div className="w-1.5 h-3 bg-yakis-gold rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
}